// ------------------ IMPORTS -------------------

// Utils / Config / Services
import { DateTime } from "luxon";

// Components
import { Dropdown, Switch } from "@lockerpm/design";
import { BadgeV2 } from "#src/commonV2/badges/Badge";
import { BadgeSeeMoreV2 } from "#src/commonV2/badges/BadgeSeeMore";
import { LoaderV2 } from "#src/commonV2/LoadingState";
import { DropdownRenderV2 } from "#src/commonV2/antdHelpers/DropdownRender";
import { TagListRenderer } from "../components/TagListRenderer";

// Child Components
import { useTimeFrameRenderer } from "./useTimeFrameRenderer";

// Others
import { useTranslation } from "react-i18next";
import { DotsVertical } from "@untitled-ui/icons-react";
import { IPassport } from "#src/services/endpoint/passports/types";
import {
  passportActionFilterItems,
  passportTypeFilterItems,
} from "#src/components/endpoint/passports/filterItems";

// ------------------ TYPES -------------------

interface UsePassportTableColumnsProps {
  switchingItem: string | null;
  canEdit: boolean;
  getTableActions: (record: IPassport) => {
    key: string;
    label: React.ReactNode;
    action: () => void;
  }[];
  onToggleStatus: (record: IPassport) => Promise<void>;
}

// ------------------ HOOK -------------------

export const usePassportTableColumns = (
  props: UsePassportTableColumnsProps
) => {
  const { switchingItem, canEdit, getTableActions, onToggleStatus } = props;
  const { t } = useTranslation("endpoint", { keyPrefix: "passports" });
  const { renderTimeFrame } = useTimeFrameRenderer();

  const columns = [
    {
      title: t("table.header.priority"),
      render: (_: IPassport, index: number) => index + 1,
      gridTemplateValue: "1fr",
      titleAlign: "center" as const,
      overwriteWrapperProps: { className: "justify-center" },
    },

    // Type
    {
      title: t("table.header.type"),
      render: (item: IPassport) => {
        const typeItem = passportTypeFilterItems.find(
          (type) => type.value === item.type
        );
        return (
          <span className="font-medium text-gray-v2-900">
            {typeItem?.getLabel()}
          </span>
        );
      },
      gridTemplateValue: "2fr",
    },

    // Action
    {
      title: t("table.header.action"),
      render: (item: IPassport) => {
        const actionItem = passportActionFilterItems.find(
          (action) => action.value === item.action
        );
        return <span>{actionItem?.getLabel()}</span>;
      },
      gridTemplateValue: "1fr",
    },

    // Time frame
    {
      title:
        t("table.header.time_frame") + ` (${DateTime.now().toFormat("ZZZZ")})`,
      render: (item: IPassport) => {
        const { content } = renderTimeFrame(item);
        return content;
      },
      gridTemplateValue: "2fr",
    },

    // Sources
    {
      title: t("table.header.sources"),
      render: (item: IPassport) => {
        return <TagListRenderer items={item.sources} />;
      },
      gridTemplateValue: "2fr",
      overwriteWrapperProps: { className: "gap-x-1 gap-y-0.5" },
    },

    // Destinations
    {
      title: t("table.header.destinations"),
      render: (item: IPassport) => {
        return <TagListRenderer items={item.destinations} />;
      },
      gridTemplateValue: "2fr",
      overwriteWrapperProps: { className: "gap-x-1 gap-y-0.5" },
    },

    // Ports
    {
      title: t("table.header.ports"),
      render: (item: IPassport) => {
        const tagList = item.ports.map((item) => item.port);
        return (
          <>
            {tagList.length > 0 ? (
              <BadgeV2 name={tagList[0]} color="gray" />
            ) : null}
            <BadgeSeeMoreV2
              badges={tagList.slice(1).map((tag) => (
                <BadgeV2 key={tag} name={tag} color="gray" />
              ))}
            />
          </>
        );
      },
      gridTemplateValue: "1fr",
      overwriteWrapperProps: { className: "gap-x-1 gap-y-0.5" },
    },

    // Status
    {
      title: t("table.header.status"),
      render: (item: IPassport) =>
        switchingItem === item.id ? (
          <LoaderV2 baseSize={2} />
        ) : (
          <Switch
            checked={item.enabled}
            disabled={!canEdit}
            onClick={(_, e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleStatus(item);
            }}
            className={canEdit ? "" : "cursor-not-allowed"}
          />
        ),
      gridTemplateValue: "1fr",
    },

    // Actions
    {
      title: null,
      render: (item: IPassport) => {
        const actions = getTableActions(item);
        const disabled = actions.length === 0;

        return (
          <Dropdown
            menu={{
              items: actions,
              onClick: ({ key, domEvent }) => {
                domEvent.preventDefault();
                domEvent.stopPropagation();
                actions.find((action) => action.key === key)?.action();
              },
            }}
            dropdownRender={(menu) => (
              <DropdownRenderV2>{menu}</DropdownRenderV2>
            )}
            trigger={["click"]}
            disabled={disabled}
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <DotsVertical
                className={`h-5 w-5 ${disabled ? "text-gray-v2-200" : "text-gray-v2-400"} transition-colors duration-300`}
              />
            </button>
          </Dropdown>
        );
      },
      overwriteWrapperProps: { className: "justify-center" },
      gridTemplateValue: "1fr",
    },
  ];

  return { columns };
};
